import DOMPurify from "dompurify";
import { resolveBlogImageSrc } from "./blogImages";
import { blogPdfFileName, resolveBlogPdfUrl, sanitizeBlogPdfPath } from "./blogPdf";

function isPdfNavigationHref(href) {
    const lower = String(href || "").trim().toLowerCase();
    if (!lower) return false;
    if (lower.includes("/uploads/blog-pdfs/")) return true;
    return /\.pdf([#?]|$)/i.test(lower);
}

/**
 * DOMPurify may drop `target` on links; keep PDF links opening in a new tab.
 */
function withPdfLinksOpenInNewTab(run) {
    const hook = (node) => {
        if (node.tagName !== "A" || !node.hasAttribute("href")) return;
        const href = node.getAttribute("href") || "";
        if (!isPdfNavigationHref(href)) return;
        node.setAttribute("target", "_blank");
        node.setAttribute("rel", "noopener noreferrer");
    };
    DOMPurify.addHook("afterSanitizeAttributes", hook);
    try {
        return run();
    } finally {
        DOMPurify.removeHook("afterSanitizeAttributes", hook);
    }
}

/**
 * Sanitize blog HTML, then expand in-body PDF placeholders from Quill
 * to a file-name link (no inline iframe).
 */
export function sanitizeBlogHtml(html) {
    return withPdfLinksOpenInNewTab(() => {
        const raw = typeof html === "string" ? html : "";
        const clean = DOMPurify.sanitize(raw, { USE_PROFILES: { html: true } });

        const parser = new DOMParser();
        const doc = parser.parseFromString(`<div class="oa-sanitize-root">${clean}</div>`, "text/html");
        const root = doc.querySelector(".oa-sanitize-root");
        if (!root) {
            return DOMPurify.sanitize(raw, {
                USE_PROFILES: { html: true },
                ADD_ATTR: ["class", "target", "rel", "href"],
                ALLOW_DATA_ATTR: true,
            });
        }

        root.querySelectorAll(".oa-pdf-pending").forEach((el) => el.remove());
        root.querySelectorAll(".oa-image-pending").forEach((el) => el.remove());

        root.querySelectorAll("img[src]").forEach((el) => {
            const src = el.getAttribute("src") || "";
            const resolved = resolveBlogImageSrc(src);
            if (resolved) {
                el.setAttribute("src", resolved);
            }
        });

        root.querySelectorAll(".oa-pdf-embed[data-pdf-url]").forEach((el) => {
            const path = sanitizeBlogPdfPath(el.getAttribute("data-pdf-url") || "");
            if (!path) {
                el.remove();
                return;
            }
            const src = resolveBlogPdfUrl(path);
            const href = src || path;
            const displayName = blogPdfFileName(path);

            const row = doc.createElement("div");
            row.className = "oa-pdf-name-row";
            const a = doc.createElement("a");
            a.className = "oa-pdf-name-link";
            a.href = href;
            a.textContent = displayName;
            a.setAttribute("target", "_blank");
            a.setAttribute("rel", "noopener noreferrer");
            row.appendChild(a);
            el.replaceWith(row);
        });

        return DOMPurify.sanitize(root.innerHTML, {
            ADD_ATTR: ["class", "target", "rel", "href", "src"],
            ALLOW_DATA_ATTR: true,
        });
    });
}
